import { Collapse, Stack, SxProps, Typography } from '@mui/material';
import { PropsWithChildren, ReactNode } from 'react';

type SidebarExpandableCardProps<T> = {
  item: T;
  expanded: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  style?: Omit<SxProps, 'children'>;
  onCardClick?: () => void;
} & PropsWithChildren;

export const SidebarExpandableCard = <T extends { name: string; id: string }>({
  item,
  expanded,
  children,
  startIcon,
  endIcon,
  style,
  onCardClick,
}: SidebarExpandableCardProps<T>) => {
  return (
    <Stack
      className={expanded ? 'expanded' : ''}
      sx={(theme) => ({
        cursor: 'pointer',
        border: theme.mixins.borderValue(),
        borderRadius: expanded ? theme.spacing(1) : undefined,

        ...(!expanded && {
          borderColor: 'transparent',
          '&:hover': {
            backgroundColor: theme.palette.background.default,
            color: theme.palette.primary.main,
            '& .MuiTypography-root': {
              color: theme.palette.primary.main,
            },
          },
          '&:last-child, &:has(+ .expanded)': { borderBottomColor: 'transparent' },
        }),

        borderBottomColor: theme.palette.common.light2,
        ...(style ?? {}),
      })}
      onClick={onCardClick}
    >
      <Stack direction="row" gap={1} py={2} px={1}>
        <Stack justifyContent="center" alignItems="center" width={30}>
          {startIcon}
        </Stack>

        <Stack overflow="hidden" flexGrow={1}>
          <Typography
            variant="h3"
            sx={(theme) => ({
              color: theme.palette.text.primary,
              whiteSpace: 'pre-wrap',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            })}
          >
            {item.name}
          </Typography>
        </Stack>

        <Stack justifyContent="center" alignItems="center">
          {endIcon}
        </Stack>
      </Stack>

      <Collapse orientation="vertical" in={expanded}>
        {children && <Stack onClick={(e) => e.stopPropagation()}>{children}</Stack>}
      </Collapse>
    </Stack>
  );
};
